import { continents } from "@/configs/map";
import { useCountries, useIsLoadingCountries } from "@/stores/country";
import type { Country } from "@/types/europafacts";
import { parseSearchParams } from "@/utils/parse";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Image,
  Menu,
  Text,
} from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";
import { IconChevronDown } from "@tabler/icons-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import "./DiscoverFilterCountry.css";

export function DiscoverFilterCountry({
  form,
  onSubmit,
}: {
  form: UseFormReturnType<{ country_id?: string[] }>;
  onSubmit: (values: { country_id?: string[] }) => void;
}) {
  const [opened, setOpened] = useState(false);
  const [searchParams] = useSearchParams();
  const countries = useCountries();
  const isLoadingCountries = useIsLoadingCountries();

  // Read filter values directly from search params for labels
  const queryValues = useMemo(
    () => parseSearchParams(searchParams),
    [searchParams]
  );
  const queryCountries = Array.isArray(queryValues.country_id)
    ? queryValues.country_id
    : [];

  const selectedCountries = Array.isArray(form.values.country_id)
    ? form.values.country_id
    : [];

  const initialCountriesRef = useRef<string[]>(
    Array.isArray(form.values.country_id) ? form.values.country_id : []
  );
  const prevOpenedRef = useRef(opened);

  // Update initial values when menu opens
  useEffect(() => {
    if (opened && !prevOpenedRef.current) {
      initialCountriesRef.current = Array.isArray(form.values.country_id)
        ? form.values.country_id
        : [];
    }
    prevOpenedRef.current = opened;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const countriesByContinent = useMemo(() => {
    if (countries.length === 0) return {};
    return groupCountriesByContinent(countries);
  }, [countries]);

  const selectedCountByContinent = useMemo(() => {
    const counts: Record<string, number> = {};
    Object.entries(countriesByContinent).forEach(
      ([continent, continentCountries]) => {
        const selectedInContinent = continentCountries.filter((country) =>
          selectedCountries.includes(country.country_id)
        ).length;
        if (selectedInContinent > 0) {
          counts[continent] = selectedInContinent;
        }
      }
    );
    return counts;
  }, [countriesByContinent, selectedCountries]);

  const isFormDirty = useMemo(() => {
    const initialCountries = Array.isArray(initialCountriesRef.current)
      ? initialCountriesRef.current
      : [];
    const currentCountries = Array.isArray(form.values.country_id)
      ? form.values.country_id
      : [];

    if (initialCountries.length !== currentCountries.length) {
      return true;
    }

    return !initialCountries.every((country: string) =>
      currentCountries.includes(country)
    );
  }, [form.values.country_id]);

  const handleCountryToggle = (countryId: string) => {
    const current = Array.isArray(form.values.country_id)
      ? form.values.country_id
      : [];
    const newCountries = current.includes(countryId)
      ? current.filter((c) => c !== countryId)
      : [...current, countryId];
    form.setFieldValue("country_id", newCountries);
  };

  const handleApply = () => {
    onSubmit(form.values);
    initialCountriesRef.current = Array.isArray(form.values.country_id)
      ? form.values.country_id
      : [];
    setOpened(false);
  };

  const handleReset = () => {
    form.setFieldValue("country_id", undefined);
    initialCountriesRef.current = [];
    onSubmit({ ...form.values, country_id: undefined });
    setOpened(false);
  };

  function getContinentForCountry(countryCode: string): string {
    const code = countryCode.toLowerCase();
    for (const continent of Object.values(continents)) {
      if (continent.countries.includes(code)) {
        return continent.name;
      }
    }
    return "Other";
  }

  function groupCountriesByContinent(
    countries: Country[]
  ): Record<string, Country[]> {
    const grouped: Record<string, Country[]> = {};

    countries.forEach((country) => {
      const continent = getContinentForCountry(country.iso2);
      if (!grouped[continent]) {
        grouped[continent] = [];
      }
      grouped[continent].push(country);
    });

    Object.keys(grouped).forEach((continent) => {
      grouped[continent].sort((a, b) =>
        a.country_name.localeCompare(b.country_name)
      );
    });

    Object.keys(grouped).forEach((continent) => {
      if (!grouped[continent]) {
        grouped[continent] = grouped[continent];
      }
    });

    return grouped;
  }

  const singleCountry =
    queryCountries.length == 1
      ? countries.find((country) => country.country_id === queryCountries[0])
      : null;

  return (
    <Menu
      closeOnItemClick={false}
      width={300}
      position="bottom-start"
      opened={opened}
      onChange={setOpened}
    >
      <Menu.Target>
        <Button
          size="xs"
          variant="subtle"
          rightSection={
            <IconChevronDown color="var(--mantine-color-gray-9)" size={12} />
          }
        >
          {singleCountry ? (
            <Flex align="center" gap={8}>
              <Image
                src={singleCountry.flag_file}
                alt={singleCountry.country_name}
                style={{
                  objectFit: "cover",
                  width: 18,
                  height: 12,
                  borderRadius: "1px",
                }}
                fit="cover"
              />
              <Text fz={15} c="eu_flag.9" fw={600}>
                {singleCountry.country_name}
              </Text>
            </Flex>
          ) : queryCountries.length > 0 ? (
            <Text fz={15} c="eu_flag.9" fw={600}>
              Countries ({queryCountries.length})
            </Text>
          ) : (
            <Text fz={15} c="gray.9" fw={400}>
              Country
            </Text>
          )}
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        {isLoadingCountries ? (
          <Menu.Item disabled>
            <Text>Loading countries...</Text>
          </Menu.Item>
        ) : (
          Object.entries(countriesByContinent).map(
            ([continent, continentCountries]) => {
              const selectedCount = selectedCountByContinent[continent] || 0;
              return (
                <Menu.Sub key={continent}>
                  <Menu.Sub.Target>
                    <Menu.Sub.Item className="continent-menu-item">
                      <Flex justify="space-between" align="center" w="100%">
                        <Text>{continent}</Text>
                        {selectedCount > 0 && (
                          <Badge size="md" variant="filled" color="blue">
                            {selectedCount}
                          </Badge>
                        )}
                      </Flex>
                    </Menu.Sub.Item>
                  </Menu.Sub.Target>
                  <Menu.Sub.Dropdown className="continent-dropdown">
                    <Box className="continent-countries-list">
                      {continentCountries.map((country) => (
                        <Menu.Item
                          key={country.country_id}
                          className="country-menu-item"
                          onClick={(e) => {
                            e.preventDefault();
                            handleCountryToggle(country.country_id);
                          }}
                        >
                          <Flex
                            className="country-item-wrapper"
                            align="center"
                            gap="xs"
                          >
                            <Image
                              src={country.flag_file}
                              alt={`${country.country_name} flag`}
                              className="country-flag"
                              w={20}
                              h={15}
                              fit="cover"
                            />
                            <Text className="country-name" flex={1}>
                              {country.country_name}
                            </Text>
                            <Checkbox
                              className="country-checkbox"
                              checked={selectedCountries.includes(
                                country.country_id
                              )}
                              onChange={() => {}}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCountryToggle(country.country_id);
                              }}
                              readOnly
                            />
                          </Flex>
                        </Menu.Item>
                      ))}
                    </Box>
                  </Menu.Sub.Dropdown>
                </Menu.Sub>
              );
            }
          )
        )}
        <Flex direction="column" p="xs" gap="sm">
          <Button
            fullWidth
            mt="sm"
            onClick={handleApply}
            disabled={!isFormDirty}
          >
            Apply
          </Button>
          <Button fullWidth variant="subtle" color="gray" onClick={handleReset}>
            Reset
          </Button>
        </Flex>
      </Menu.Dropdown>
    </Menu>
  );
}
